import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  IconButton,
  Avatar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import api, { uploadsUrl } from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { tokens } from '../../theme/tokens';

export default function ElectionCandidates() {
  const { electionId } = useParams();
  const navigate = useNavigate();
  const [election, setElection] = useState(null);
  const [positions, setPositions] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [formData, setFormData] = useState({
    position_id: '',
    candidate_member_id: '',
    manifesto: '',
  });
  const [candidatePhoto, setCandidatePhoto] = useState(null);

  useEffect(() => {
    if (electionId) {
      fetchElectionDetails();
      fetchMembers();
    }
  }, [electionId]);

  const fetchElectionDetails = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await api.get(`/hr/elections/${electionId}`);
      setElection(response.data.data);
      setPositions(response.data.data.positions || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch election details');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await api.get('/members', {
        params: { limit: 1000, status: 'Active' },
      });
      setMembers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const handleOpenDialog = (position = null) => {
    setEditingCandidate(null);
    setSelectedPosition(position);
    setFormData({
      position_id: position?.id || '',
      candidate_member_id: '',
      manifesto: '',
    });
    setCandidatePhoto(null);
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (candidate, position) => {
    setEditingCandidate(candidate);
    setSelectedPosition(position);
    setFormData({
      position_id: position?.id || '',
      candidate_member_id: candidate.candidate_member_id,
      manifesto: candidate.manifesto || '',
    });
    setCandidatePhoto(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCandidate(null);
    setSelectedPosition(null);
    setCandidatePhoto(null);
    setFormData({
      position_id: '',
      candidate_member_id: '',
      manifesto: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      if (editingCandidate) {
        const payload = new FormData();
        payload.append('manifesto', formData.manifesto);
        if (candidatePhoto) payload.append('candidate_photo', candidatePhoto);
        await api.put(`/hr/elections/${electionId}/candidates/${editingCandidate.id}`, payload);
      } else {
        const payload = new FormData();
        payload.append('position_id', formData.position_id);
        payload.append('candidate_member_id', formData.candidate_member_id);
        if (formData.manifesto) payload.append('manifesto', formData.manifesto);
        if (candidatePhoto) payload.append('candidate_photo', candidatePhoto);
        await api.post(`/hr/elections/${electionId}/candidates`, payload);
      }
      handleCloseDialog();
      await fetchElectionDetails({ silent: true });
    } catch (err) {
      setError(err.response?.data?.message || (editingCandidate ? 'Failed to update candidate' : 'Failed to add candidate'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCandidate = async (candidateId) => {
    if (!window.confirm('Are you sure you want to remove this candidate?')) {
      return;
    }
    try {
      await api.delete(`/hr/elections/${electionId}/candidates/${candidateId}`);
      await fetchElectionDetails({ silent: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove candidate');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!election) {
    return (
      <Alert severity="error">
        Election not found
      </Alert>
    );
  }

  const selectedPositionCandidates =
    positions.find((p) => String(p.id) === String(formData.position_id))?.candidates || [];
  const alreadyCandidateIds = new Set(
    selectedPositionCandidates.map((c) => c.candidate_member_id)
  );
  const availableMembers = editingCandidate
    ? members
    : members.filter((m) => !alreadyCandidateIds.has(m.id));
  const selectedMember =
    availableMembers.find((m) => String(m.id) === String(formData.candidate_member_id)) || null;

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Election Candidates</Box>
          </>
        }
        title={`Candidates: ${election.name}`}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/hr/elections')}
            variant="outlined"
            sx={{ borderColor: tokens.border, color: tokens.ink }}
          >
            Back to Elections
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {positions.length === 0 && !loading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This election has no positions yet. Add positions when creating or editing the election, then you can add candidates.
          <Button size="small" sx={{ ml: 1 }} onClick={() => navigate('/dashboard/hr/elections')}>
            Back to Elections
          </Button>
        </Alert>
      )}

      {positions.map((position) => (
        <Card key={position.id} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">{position.name}</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog(position)}
              >
                Add Candidate
              </Button>
            </Box>

            {position.candidates && position.candidates.length > 0 ? (
              <Grid container spacing={2}>
                {position.candidates.map((candidate) => (
                  <Grid item xs={12} sm={6} md={4} key={candidate.id}>
                    <Paper sx={{ overflow: 'hidden', position: 'relative' }}>
                      <Box sx={{ bgcolor: 'grey.100', py: 2, display: 'flex', justifyContent: 'center' }}>
                        <Avatar
                          src={uploadsUrl(candidate.candidate_photo) || undefined}
                          sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}
                        >
                          {!candidate.candidate_photo && (candidate.first_name?.[0] || '') + (candidate.last_name?.[0] || '')}
                        </Avatar>
                      </Box>
                      <CardContent sx={{ pt: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="600">
                              {candidate.first_name} {candidate.last_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {candidate.email}
                            </Typography>
                          </Box>
                          <Box>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditDialog(candidate, position)}
                              aria-label="Edit candidate"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveCandidate(candidate.id)}
                              aria-label="Remove candidate"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                        {candidate.manifesto && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} noWrap>
                            {candidate.manifesto}
                          </Typography>
                        )}
                        {candidate.vote_count !== undefined && (
                          <Chip
                            label={`${candidate.vote_count} votes`}
                            size="small"
                            sx={{ mt: 1 }}
                            color="primary"
                            variant="outlined"
                          />
                        )}
                      </CardContent>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No candidates registered for this position
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        aria-labelledby="candidate-dialog-title"
        aria-describedby="candidate-dialog-description"
      >
        <DialogTitle id="candidate-dialog-title">
          {editingCandidate ? 'Edit Candidate' : 'Add Candidate'}
        </DialogTitle>
        <DialogContent id="candidate-dialog-description">
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }} disabled={!!editingCandidate}>
              <InputLabel>Position</InputLabel>
              <Select
                value={formData.position_id}
                onChange={(e) => setFormData({ ...formData, position_id: e.target.value })}
                label="Position"
                disabled={!!selectedPosition || !!editingCandidate}
                autoFocus={!editingCandidate && !selectedPosition}
              >
                {positions.map((pos) => (
                  <MenuItem key={pos.id} value={pos.id}>
                    {pos.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              fullWidth
              sx={{ mb: 2 }}
              disabled={!!editingCandidate}
              options={availableMembers}
              value={selectedMember}
              onChange={(_, member) =>
                setFormData({
                  ...formData,
                  candidate_member_id: member?.id || '',
                })
              }
              getOptionLabel={(m) =>
                `${m.first_name || ''} ${m.last_name || ''} (${m.member_id || m.email || ''})`.trim()
              }
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((m) => {
                  const name = `${m.first_name || ''} ${m.middle_name || ''} ${m.last_name || ''}`.toLowerCase();
                  const memberId = String(m.member_id || '').toLowerCase();
                  const email = String(m.email || '').toLowerCase();
                  return name.includes(q) || memberId.includes(q) || email.includes(q);
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Candidate (Member)"
                  placeholder="Search by name or member ID"
                  autoFocus={!editingCandidate && !!selectedPosition}
                />
              )}
            />

            <TextField
              fullWidth
              label="Manifesto (Optional)"
              value={formData.manifesto}
              onChange={(e) => setFormData({ ...formData, manifesto: e.target.value })}
              multiline
              rows={4}
            />

            <Button variant="outlined" component="label" sx={{ mt: 2 }}>
              {candidatePhoto ? candidatePhoto.name : (editingCandidate ? 'Change candidate photo (optional)' : 'Upload candidate photo (optional)')}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setCandidatePhoto(e.target.files?.[0] || null)}
              />
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              saving ||
              (!editingCandidate && (!formData.position_id || !formData.candidate_member_id))
            }
          >
            {saving
              ? (editingCandidate ? 'Saving...' : 'Adding...')
              : (editingCandidate ? 'Save Changes' : 'Add Candidate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
